import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Trophy, BarChart3, ChevronDown, ChevronUp, Heart, MessageSquare, Eye } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOpts = [
  { value: "draft", label: "Entwurf", cls: "bg-gray-100 text-gray-700" },
  { value: "running", label: "Aktiv", cls: "bg-green-100 text-green-800" },
  { value: "completed", label: "Abgeschlossen", cls: "bg-blue-100 text-blue-800" },
];

const emptyForm = { company_id: "", name: "", description: "", variant_ids: [] };

export default function ABTestPage() {
  const [tests, setTests] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [content, setContent] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [expanded, setExpanded] = useState(null);
  const [testDetails, setTestDetails] = useState({});
  const { t } = useLanguage();

  const load = async () => {
    try {
      const [t, co, c] = await Promise.all([
        axios.get(`${API}/ab-tests`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }),
        axios.get(`${API}/content`, { withCredentials: true }),
      ]);
      setTests(t.data);
      setCompanies(co.data);
      setContent(c.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const loadDetails = async (id) => {
    try {
      const res = await axios.get(`${API}/ab-tests/${id}`, { withCredentials: true });
      setTestDetails((p) => ({ ...p, [id]: res.data }));
    } catch (e) { console.error(e); }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API}/ab-tests`, form, { withCredentials: true });
      toast.success("A/B Test erstellt");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch { toast.error("Fehler"); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/ab-tests/${id}`, { status }, { withCredentials: true });
      toast.success("Status aktualisiert");
      load();
    } catch { toast.error("Fehler"); }
  };

  const setWinner = async (testId, winnerId) => {
    try {
      await axios.put(`${API}/ab-tests/${testId}`, { winner_id: winnerId, status: "completed" }, { withCredentials: true });
      toast.success("Gewinner festgelegt!");
      load();
      loadDetails(testId);
    } catch { toast.error("Fehler"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("A/B Test loeschen?")) return;
    try {
      await axios.delete(`${API}/ab-tests/${id}`, { withCredentials: true });
      toast.success("Geloescht");
      load();
    } catch { toast.error("Fehler"); }
  };

  const toggleExpand = (id) => {
    if (expanded === id) {
      setExpanded(null);
    } else {
      setExpanded(id);
      if (!testDetails[id]) loadDetails(id);
    }
  };

  const toggleVariant = (id) => {
    setForm((f) => ({
      ...f,
      variant_ids: f.variant_ids.includes(id)
        ? f.variant_ids.filter((v) => v !== id)
        : [...f.variant_ids, id],
    }));
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "Unbekannt";
  const companyContent = form.company_id ? content.filter((c) => c.company_id === form.company_id) : [];

  return (
    <div data-testid="ab-test-page" className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight">{t("ab.title")}</h1>
          <p className="text-[var(--studio-text-muted)] mt-1">{t("ab.subtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(emptyForm); }}>
          <DialogTrigger asChild>
            <Button data-testid="create-ab-test-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
              <Plus className="w-4 h-4 mr-2" /> {t("ab.create_btn")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Neuen A/B Test erstellen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs text-[var(--studio-text-muted)]">Firma *</Label>
                <Select value={form.company_id} onValueChange={(v) => setForm((f) => ({ ...f, company_id: v, variant_ids: [] }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Firma waehlen" /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[var(--studio-text-muted)]">Test-Name *</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1"
                  placeholder="z.B. Caption-Vergleich Fruehlingspost" />
              </div>
              <div>
                <Label className="text-xs text-[var(--studio-text-muted)]">Beschreibung</Label>
                <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-1" rows={2} />
              </div>
              <div>
                <Label className="text-xs text-[var(--studio-text-muted)]">Varianten waehlen (min. 2 Content-Items)</Label>
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                  {companyContent.length === 0 ? (
                    <p className="text-xs text-[var(--studio-text-muted)]">Keine Content-Items fuer diese Firma.</p>
                  ) : (
                    companyContent.map((c) => (
                      <button key={c.id} onClick={() => toggleVariant(c.id)}
                        className={`w-full text-left p-2 rounded border text-xs transition-all ${
                          form.variant_ids.includes(c.id)
                            ? "border-[#2563EB] bg-[#2563EB]/5"
                            : "border-[var(--studio-border)]"
                        }`}>
                        <span className="font-medium">{c.format_type || c.type}</span>
                        <span className="text-[var(--studio-text-muted)]"> &middot; {c.goal} &middot; {c.status}</span>
                      </button>
                    ))
                  )}
                </div>
                <p className="text-xs text-[var(--studio-text-muted)] mt-1">{form.variant_ids.length} ausgewaehlt</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => { setOpen(false); setForm(emptyForm); }}>Abbrechen</Button>
              <Button onClick={handleSave} data-testid="save-ab-test-btn"
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                disabled={!form.name || !form.company_id || form.variant_ids.length < 2}>
                Erstellen
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {tests.length === 0 ? (
        <Card className="border-[var(--studio-border)] shadow-none">
          <CardContent className="py-16 text-center">
            <BarChart3 className="w-12 h-12 mx-auto text-[var(--studio-text-muted)] opacity-40 mb-4" />
            <p className="text-[var(--studio-text-muted)]">Noch keine A/B Tests erstellt.</p>
            <p className="text-sm text-[var(--studio-text-muted)] mt-1">Vergleiche verschiedene Content-Varianten.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {tests.map((test) => {
            const st = statusOpts.find((s) => s.value === test.status) || statusOpts[0];
            const details = testDetails[test.id];
            return (
              <Card key={test.id} className="border-[var(--studio-border)] shadow-none" data-testid={`ab-test-${test.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium font-heading">{test.name}</h3>
                        <Badge className={`${st.cls} border-0 text-xs`}>{st.label}</Badge>
                        {test.winner_id && <Badge className="bg-amber-100 text-amber-800 border-0 text-xs"><Trophy className="w-3 h-3 mr-0.5 inline" /> Gewinner</Badge>}
                      </div>
                      <p className="text-xs text-[var(--studio-text-muted)]">
                        {companyName(test.company_id)} &middot; {test.variant_ids?.length || 0} Varianten
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {test.status === "draft" && (
                        <Button size="sm" onClick={() => updateStatus(test.id, "running")}
                          className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs">Starten</Button>
                      )}
                      {test.status === "running" && (
                        <Button size="sm" onClick={() => updateStatus(test.id, "completed")}
                          className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs">Beenden</Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => toggleExpand(test.id)}>
                        {expanded === test.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(test.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>

                  {expanded === test.id && details?.variants && (
                    <div className="mt-4 pt-3 border-t border-[var(--studio-border)] space-y-3 animate-fade-in">
                      <p className="text-sm font-medium">Varianten-Vergleich:</p>
                      {details.variants.map((v, i) => {
                        const c = v.content;
                        const m = v.metrics?.metrics || {};
                        const isWinner = test.winner_id === c?.id;
                        return c ? (
                          <div key={i} className={`p-3 rounded-lg ${isWinner ? "bg-amber-50 border border-amber-200" : "bg-[var(--studio-bg)]"}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">Variante {String.fromCharCode(65 + i)}</span>
                                <Badge variant="outline" className="text-[10px]">{c.format_type}</Badge>
                                {isWinner && <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]"><Trophy className="w-2.5 h-2.5 mr-0.5 inline" /> Gewinner</Badge>}
                              </div>
                              {!test.winner_id && test.status !== "draft" && (
                                <Button size="sm" variant="outline" className="h-6 text-[10px]"
                                  onClick={() => setWinner(test.id, c.id)}>
                                  <Trophy className="w-3 h-3 mr-1" /> Als Gewinner
                                </Button>
                              )}
                            </div>
                            <p className="text-xs text-[var(--studio-text-muted)] line-clamp-2">
                              {c.selected_content?.caption || c.generated_content?.captions?.[0] || "Kein Text"}
                            </p>
                            <div className="flex gap-4 mt-2 text-xs text-[var(--studio-text-muted)]">
                              <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-pink-500" /> {m.likes || 0}</span>
                              <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3 text-blue-500" /> {m.comments || 0}</span>
                              <span className="flex items-center gap-1"><Eye className="w-3 h-3 text-green-500" /> {m.reach || 0}</span>
                            </div>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
